import { Button } from "@/components/Button/Button";
import { ProgressBar } from "@/components/ProgressBar/ProgressBar";
import { RootState } from "@/store/store";
import { Box, Theme, Typography } from "@mui/material";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

interface ProgressProps {
  progressSelector: (state: RootState) => number | undefined;
  error: boolean;
}

const Progress: FC<ProgressProps> = ({ progressSelector, error }) => {
  const progress = useSelector(progressSelector);

  return (
    <ProgressBar value={progress} variant={progress === undefined ? "indeterminate" : "determinate"} color={error ? "error" : "success"} />
  );
};

interface ProgressRowProps {
  name: string;
  complete?: boolean;
  error: boolean;
  cancelled?: boolean;
  handleOnClickCancel?: () => void;
  handleOnClickRetry?: () => void;
  progressSelector: (state: RootState) => number | undefined;
}

export const ProgressRow: FC<ProgressRowProps> = ({
  name,
  complete,
  error,
  cancelled,
  handleOnClickCancel,
  handleOnClickRetry,
  progressSelector
}) => {
  const { t } = useTranslation("cases");

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        padding: "12px",
        borderTop: (theme: Theme) => `1px solid ${theme.outline.main}`,
        borderBottom: (theme: Theme) => `1px solid ${theme.outline.main}`,
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        backgroundColor: (theme: Theme) => theme.surface.low,
        marginBottom: "-1px" /* prevent double border from previous item */
      }}
    >
      <Box
        component="div"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%"
        }}
      >
        <Typography
          sx={{
            maxWidth: "220px",
            maxLines: "1",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            ...(cancelled && { maxWidth: "100%" })
          }}
          variant="body2"
        >
          {cancelled ? t("label.scanUploadCancelled", { scanName: name }) : name}
        </Typography>
        {!cancelled && !complete && (
          <Button
            variant="text"
            size="small"
            className="actionButton"
            onClick={error ? handleOnClickRetry : !complete ? handleOnClickCancel : undefined}
          >
            {error ? t("button.retry") : t("button.cancel")}
          </Button>
        )}
      </Box>
      {!cancelled && !complete && <Progress progressSelector={progressSelector} error={error} />}
    </Box>
  );
};
